import { SIMAElementClassifier, SIMARouteType, SIMA as SIMADoc, } from "@earthbrain/sima";
import { Cartesian3 } from "cesium";
import { generateMidpointLabel } from "../cross-sections/linework-cross-section-handler-helper";
import { SIMA } from "../SIMA/sima";
import { PointType, } from "../site/arbitrary-line/types";
import { findNearestPathPointIndex, interpolateAt, prepareCenterlineData, } from "../site/mid-path/mid-path-interpolation";
import { SegmentType } from "../types";
export class SIMAAdapter {
    prepareSIMAdata(json, siteName = "") {
        const linearData = JSON.parse(json);
        const surveyPoints = [];
        let routeName = "IP線";
        // Version 2.0 and above: pathInfo-based (all pathPoints + control point labels)
        if (linearData.version &&
            linearData.version >= 2.0 &&
            "pathInfo" in linearData &&
            linearData.pathInfo &&
            linearData.pathInfo.pathPoints) {
            console.log("[SIMA] Using Version 2.0: pathInfo-based export with control point labels");
            routeName = "中心線形";
            const pathPoints = linearData.pathInfo.pathPoints;
            const controlPoints = linearData.controlPoints || [];
            const segments = linearData.pathInfo.segments || [];
            // Create control point position map (for BP/SP/EP label assignment)
            const controlPointLabels = new Map();
            let spCounter = 0;
            // Generate BP/SP/EP labels
            for (const controlPoint of controlPoints) {
                const controlPointPosition = new Cartesian3(controlPoint.point[0], controlPoint.point[1], controlPoint.point[2]);
                const nearestIndex = findNearestPathPointIndex(controlPointPosition, pathPoints);
                if (nearestIndex !== -1) {
                    let label = null;
                    if (controlPoint.pointType === PointType.BEGINNING_POINT) {
                        label = "BP";
                    }
                    else if (controlPoint.pointType === PointType.END_POINT) {
                        label = "EP";
                    }
                    else if (controlPoint.pointType === PointType.INTERMEDIATE_POINT) {
                        spCounter++;
                        label = `SP${spCounter}`;
                    }
                    if (label) {
                        controlPointLabels.set(nearestIndex, label);
                    }
                }
            }
            // Generate BC/EC labels (only CURVE segment midpoints - using cross-section logic)
            if (segments && segments.length > 0) {
                let cumulativeDistance = 0;
                const segmentStartDistances = [0];
                for (const segment of segments) {
                    cumulativeDistance += segment.length;
                    segmentStartDistances.push(cumulativeDistance);
                }
                // Process only CURVE segments
                for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
                    const segment = segments[segmentIndex];
                    if (segment.type === SegmentType.CURVE) {
                        const segmentStartDist = segmentStartDistances[segmentIndex];
                        const segmentMidDist = segmentStartDist + segment.length / 2;
                        const centerlineData = prepareCenterlineData(pathPoints);
                        const midpointResult = interpolateAt(centerlineData, segmentMidDist);
                        const midpointIndex = findNearestPathPointIndex(midpointResult.position, pathPoints);
                        if (midpointIndex !== -1) {
                            const bcEcLabel = generateMidpointLabel(segment.id, segments);
                            const existingLabel = controlPointLabels.get(midpointIndex);
                            if (existingLabel) {
                                controlPointLabels.set(midpointIndex, `${existingLabel}/${bcEcLabel}`);
                            }
                            else {
                                controlPointLabels.set(midpointIndex, bcEcLabel);
                            }
                        }
                    }
                }
            }
            // Output all pathPoints
            for (let index = 0; index < pathPoints.length; index++) {
                const pathPoint = pathPoints[index];
                const label = controlPointLabels.get(index);
                surveyPoints.push({
                    classifier: SIMAElementClassifier.SURVEY_POINT_COORD,
                    point_number: index + 1,
                    point_name: label || "",
                    x: pathPoint.position.x,
                    y: pathPoint.position.y,
                    z: pathPoint.position.z,
                });
            }
        }
        else {
            // Version 1.0: Control point-based (legacy)
            console.log("[SIMA] Using Version 1.0: control points-based export");
            routeName = "IP線";
            const controlPoints = linearData.controlPoints || [];
            let counter = 0;
            controlPoints.forEach((controlPoint, index) => {
                const label = controlPoint.pointType === PointType.INTERMEDIATE_POINT
                    ? `IP.${(++counter).toString()}`
                    : controlPoint.pointType === PointType.BEGINNING_POINT
                        ? "BP"
                        : "EP";
                surveyPoints.push({
                    classifier: SIMAElementClassifier.SURVEY_POINT_COORD,
                    point_number: index + 1,
                    point_name: label,
                    x: controlPoint.point[0],
                    y: controlPoint.point[1],
                    z: controlPoint.point[2],
                });
            });
        }
        const routeElements = surveyPoints.map((sp) => ({
            classifier: SIMAElementClassifier.ROUTE_COORD,
            point_number: sp.point_number,
            point_name: sp.point_name,
        }));
        const routeAttribute = {
            classifier: SIMAElementClassifier.ROUTE_BEGIN,
            route_number: 1,
            group_number: 1,
            route_name: routeName,
            route_type: SIMARouteType.IP_LINE,
        };
        const route = {
            routeAttributes: [routeAttribute],
            routeElements,
        };
        const simaFormat = {
            comment: {
                classifier: SIMAElementClassifier.COMMENT,
            },
            fileAttributeData: {
                classifier: SIMAElementClassifier.FILE_ATTRIBUTE,
                version: 2,
                site_name: siteName,
            },
            surveyPoints,
            simaRoutes: [route],
        };
        const simaDoc = SIMADoc.fromFormat(simaFormat);
        return SIMA.fromSIMADoc(simaDoc);
    }
}
//# sourceMappingURL=sima-adapter.js.map